import { ORPCError } from "@orpc/client";
import { Loader2, Sparkles } from "lucide-react";
import type { JSX } from "react";
import { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { Template } from "@/common/types/templates";
import { AssistantActions } from "@/components/ui/AssistantActions";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "@/components/ui/ConfirmDialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { WithDictation } from "@/components/ui/DictationButton";
import { DictationTextarea } from "@/components/ui/DictationTextarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useTemplateProposal } from "@/hooks/templates/useTemplateProposal";
import { useTemplateWrites } from "@/hooks/templates/useTemplateWrites";
import { toast } from "@/hooks/use-toast";
import { changeText } from "@/lib/assistantChangeStyles";
import { isStaleProposalError } from "@/lib/isStaleProposalError";
import { refreshTemplateReads, toTemplate } from "@/lib/template-queries";
import { cn } from "@/lib/utils";
import { getVisitScope, type VisitScope } from "@/lib/visit-queries";
import type { TemplateFormSchemaType } from "@/schemas/template.schema";

// DoctorTemplate.name column limit (the backend trims to this as well).
const NAME_MAX_LENGTH = 100;

const SECTION_LABEL =
  "text-xs font-semibold uppercase tracking-wide text-muted-foreground";

const SectionLabel = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => <p className={SECTION_LABEL}>{children}</p>;

const ProposalSkeleton = (): JSX.Element => (
  <div
    aria-hidden
    className="space-y-3 rounded-lg border bg-muted/30 p-4"
    data-testid="proposal-skeleton"
  >
    <Skeleton className="h-4 w-1/3" />
    <Skeleton className="h-4 w-full" />
    <div className="flex flex-wrap gap-2">
      <Skeleton className="h-7 w-24 rounded-full" />
      <Skeleton className="h-7 w-32 rounded-full" />
      <Skeleton className="h-7 w-20 rounded-full" />
    </div>
    <Skeleton className="h-20 w-full" />
  </div>
);

export type TemplateAssistantPanelProps = {
  templatePublicId?: string;
  onSaved: (template: Template, scope: VisitScope | null) => void;
};

// Lets the provider describe a template (typed or dictated), review the
// proposed name/headings/rules with what changed, then either apply it to the
// form below or save it after confirming. Nothing is saved without a click.
export const TemplateAssistantPanel = ({
  templatePublicId,
  onSaved,
}: TemplateAssistantPanelProps): JSX.Element => {
  const { t } = useTranslation();
  const form = useFormContext<TemplateFormSchemaType>();
  const { create, update, loading: writing } = useTemplateWrites();
  const [instruction, setInstruction] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isDictating, setIsDictating] = useState(false);
  const [showAllRules, setShowAllRules] = useState(false);
  // The proposed name, editable before applying or saving.
  const [proposedName, setProposedName] = useState("");

  const [name, headers, optionalText] = useWatch({
    control: form.control,
    name: ["name", "headers", "optionalText"],
  });
  const draft = {
    name: name ?? "",
    headers: headers ?? [],
    rules: optionalText ?? "",
  };
  const proposal = useTemplateProposal({
    instruction,
    templatePublicId,
    current:
      draft.name || draft.headers.length || draft.rules ? draft : undefined,
  });
  const { result, proposing, failed: proposeFailed } = proposal;
  useEffect(() => {
    setProposedName(result?.proposal.name ?? "");
    if (!result) {
      setConfirmOpen(false);
      setSaving(false);
    }
  }, [result]);
  const trimmedName = proposedName.trim();
  const nameMissing = !trimmedName;
  const handlePropose = async (): Promise<void> => {
    setShowAllRules(false);
    setConfirmOpen(false);
    await proposal.propose();
  };

  const handleApply = (): void => {
    if (!result || nameMissing || !proposal.isCurrent()) return;
    const { headers, rules } = result.proposal;
    const options = { shouldDirty: true, shouldValidate: true };
    form.setValue("name", trimmedName, options);
    form.setValue("headers", headers, options);
    form.setValue("optionalText", rules, options);
    proposal.clear();
    setInstruction("");
    toast({
      variant: "default",
      title: t("templates.assistant.appliedToast"),
    });
  };

  const handleSave = async (): Promise<void> => {
    const scope = getVisitScope();
    if (!result || nameMissing || !scope || !proposal.isCurrent() || writing)
      return;
    const current = proposal.isCurrent;
    setSaving(true);
    let writeAttempted = false;
    try {
      const { headers, rules } = result.proposal;
      const name = trimmedName;
      // Retain the assistant's storable-content bounds alongside shared write validation.
      if (
        !name.trim() ||
        (!headers.length && !rules.trim()) ||
        headers.join(",").length > 500 ||
        rules.length > 10000
      )
        throw Error("Invalid assistant template");
      if (templatePublicId !== undefined && !result.baseVersion)
        throw Error("Proposal version required");
      const definition = { name, headers, optionalText: rules };
      const outcome =
        templatePublicId === undefined
          ? await create({ ...definition, isDefault: false })
          : await update(templatePublicId, {
              ...definition,
              baseVersion: result.baseVersion,
            });
      if (!outcome) return;
      if (!outcome.ok) {
        writeAttempted = outcome.attempted;
        throw outcome.error;
      }
      writeAttempted = true;
      const saved = toTemplate(outcome.value);
      if (!current()) {
        void refreshTemplateReads(scope);
        return;
      }
      proposal.clear();
      onSaved(saved, scope);
    } catch (error) {
      const uncertain =
        writeAttempted &&
        !(
          error instanceof ORPCError &&
          [
            "BAD_REQUEST",
            "UNAUTHORIZED",
            "ACCOUNT_UNAVAILABLE",
            "FORBIDDEN",
            "NOT_FOUND",
            "METHOD_NOT_ALLOWED",
            "CONFLICT",
          ].includes(error.code)
        );
      if (uncertain) void refreshTemplateReads(scope);
      if (!current()) return;
      if (templatePublicId === undefined && uncertain) {
        proposal.clear();
        toast({
          variant: "error",
          title: t("templates.assistant.createUncertainTitle"),
          description: t("templates.assistant.createUncertain"),
        });
        return;
      }
      if (isStaleProposalError(error)) {
        proposal.clear();
        toast({
          variant: "error",
          title: t("templates.assistant.staleError"),
        });
      } else {
        toast({ variant: "error", title: t("templates.assistant.saveError") });
      }
    } finally {
      if (current()) setSaving(false);
    }
  };

  const diff = result?.diff;
  const isEdit = (diff?.headers.before.length ?? 0) > 0;

  return (
    <Card className="overflow-hidden" data-testid="template-assistant">
      <CardHeader className="space-y-1.5 p-4 sm:p-6">
        <CardTitle className="flex items-center gap-2 text-lg">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
            <Sparkles className="h-4 w-4 text-primary" />
          </span>
          {t("templates.assistant.title")}
        </CardTitle>
        <CardDescription>
          {t("templates.assistant.description")}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4 p-4 pt-0 sm:p-6 sm:pt-0">
        <div className="space-y-2">
          <Label htmlFor="assistant-instruction">
            {t("templates.assistant.instructionLabel")}
          </Label>
          <DictationTextarea
            id="assistant-instruction"
            placeholder={t("templates.assistant.instructionPlaceholder")}
            value={instruction}
            onChange={setInstruction}
            onDictatingChange={setIsDictating}
          />
        </div>

        <div className="flex justify-end">
          <Button
            className="h-11 w-full sm:h-9 sm:w-auto"
            disabled={!instruction.trim() || proposing || isDictating}
            type="button"
            onClick={() => void handlePropose()}
          >
            {proposing && <Loader2 className="animate-spin" />}
            {t(
              proposing
                ? "templates.assistant.proposing"
                : "templates.assistant.proposeButton",
            )}
          </Button>
        </div>

        {proposeFailed && !proposing && (
          <div
            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            role="alert"
          >
            {t("templates.assistant.proposeError")}
            <Button
              className="h-11 sm:h-8"
              size="sm"
              type="button"
              variant="outline"
              onClick={() => void handlePropose()}
            >
              {t("templates.assistant.retry")}
            </Button>
          </div>
        )}

        {proposing && <ProposalSkeleton />}

        {result && diff && !proposing && (
          <section
            aria-label={t("templates.assistant.proposalTitle")}
            className="space-y-5 rounded-lg border bg-muted/30 p-4"
            data-testid="template-proposal"
          >
            <div className="space-y-1">
              <h3 className="text-sm font-semibold">
                {t("templates.assistant.proposalTitle")}
              </h3>
              <p className="text-sm text-muted-foreground">
                {result.summaryOfChanges}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label
                className={SECTION_LABEL}
                htmlFor="assistant-template-name"
              >
                {t("templates.createTemplateForm.templateNameLabel")}
              </Label>
              {diff.name.changed && diff.name.before && (
                <p className="wrap-break-word text-sm">
                  <span className="sr-only">
                    {t("templates.assistant.previousName")}{" "}
                  </span>
                  <span className={changeText.before}>{diff.name.before}</span>
                </p>
              )}
              <WithDictation
                fieldLabel={t("dictation.field.fields.templateName")}
              >
                <Input
                  aria-describedby="assistant-template-name-hint"
                  aria-invalid={nameMissing}
                  className="font-medium"
                  id="assistant-template-name"
                  maxLength={NAME_MAX_LENGTH}
                  value={proposedName}
                  onChange={(event) => {
                    setProposedName(event.target.value);
                  }}
                />
              </WithDictation>
              <p
                id="assistant-template-name-hint"
                role={nameMissing ? "alert" : undefined}
                className={cn(
                  "text-xs",
                  nameMissing ? changeText.dangerText : "text-muted-foreground",
                )}
              >
                {t(
                  nameMissing
                    ? "templates.assistant.nameRequired"
                    : "templates.assistant.nameHint",
                )}
              </p>
            </div>

            <div className="space-y-2">
              <SectionLabel>
                {t("templates.assistant.headingsLabel")}
                {diff.headers.reordered && (
                  <span className="ml-1 font-normal normal-case">
                    · {t("templates.assistant.reordered")}
                  </span>
                )}
              </SectionLabel>
              <ol className="flex flex-wrap gap-2">
                {diff.headers.after.map((heading) => {
                  const added = isEdit && diff.headers.added.includes(heading);
                  return (
                    <li
                      key={heading}
                      className={cn(
                        "rounded-full border px-3 py-1 text-sm",
                        added
                          ? cn("font-medium", changeText.addedChip)
                          : "border-transparent bg-background",
                      )}
                    >
                      {added && <span aria-hidden>+ </span>}
                      {heading}
                      {added && (
                        <span className="sr-only">
                          {" "}
                          ({t("templates.assistant.added")})
                        </span>
                      )}
                    </li>
                  );
                })}
                {diff.headers.removed.map((heading) => (
                  <li
                    key={`removed-${heading}`}
                    className={cn(
                      "rounded-full border px-3 py-1 text-sm",
                      changeText.removedChip,
                    )}
                  >
                    {heading}
                    <span className="sr-only">
                      {" "}
                      ({t("templates.assistant.removed")})
                    </span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="space-y-1">
              <SectionLabel>
                {t("templates.createTemplateForm.instructionsLabel")}
                {diff.rules.before !== null && (
                  <span className="ml-1 font-normal normal-case">
                    ·{" "}
                    {t(
                      diff.rules.changed
                        ? "templates.assistant.changed"
                        : "templates.assistant.unchanged",
                    )}
                  </span>
                )}
              </SectionLabel>
              <div className="rounded-md border bg-background p-3 text-sm leading-relaxed">
                <p
                  className={cn(
                    "whitespace-pre-wrap wrap-break-word",
                    !showAllRules && "line-clamp-6",
                  )}
                >
                  {diff.rules.after}
                </p>
              </div>
              <Button
                className="h-11 px-0 sm:h-8"
                size="sm"
                type="button"
                variant="link"
                onClick={() => {
                  setShowAllRules(!showAllRules);
                }}
              >
                {t(
                  showAllRules
                    ? "templates.assistant.showLess"
                    : "templates.assistant.showAll",
                )}
              </Button>
            </div>

            <AssistantActions>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  proposal.clear();
                }}
              >
                {t("templates.assistant.discardButton")}
              </Button>
              <Button
                disabled={nameMissing}
                type="button"
                variant="outline"
                onClick={handleApply}
              >
                {t("templates.assistant.applyButton")}
              </Button>
              <Button
                className="order-first col-span-2 sm:order-0"
                disabled={saving || writing || nameMissing}
                type="button"
                onClick={() => {
                  setConfirmOpen(true);
                }}
              >
                {(saving || writing) && <Loader2 className="animate-spin" />}
                {t("templates.assistant.saveButton")}
              </Button>
            </AssistantActions>
          </section>
        )}
      </CardContent>

      <ConfirmDialog
        confirmText={t("templates.assistant.saveButton")}
        open={confirmOpen && !!result}
        title={t("templates.assistant.confirmTitle")}
        description={t(
          templatePublicId
            ? "templates.assistant.confirmUpdate"
            : "templates.assistant.confirmCreate",
          { name: trimmedName },
        )}
        onConfirm={() => void handleSave()}
        onOpenChange={setConfirmOpen}
      />
    </Card>
  );
};
